import { ONBOARDING_STEP } from "../content/onboarding";
import { evaluateBuild } from "../content/skill-paths";
import type { BankedProfile } from "./persistence";
import type { WorldSnapshot } from "./snapshot";
import { activeSortieContract } from "./sortie-contracts";

export type SortieObjective = {
  title: string;
  detail: string;
  tone: "hunt" | "extract" | "ops" | "build" | "coach";
};

export function currentObjective(snap: WorldSnapshot, banked: BankedProfile): SortieObjective {
  if (snap.phase === "onboarding" && snap.onboardingStep < ONBOARDING_STEP.DONE) {
    return {
      title: "TRAINING",
      detail: snap.message || "Follow the coach beat.",
      tone: "coach",
    };
  }
  if (snap.runProgress.unspentSkillPoints > 0 || snap.runProgress.skillPickOffers.length > 0) {
    const verdict = evaluateBuild(snap.runProgress.takenNodes);
    return {
      title: "LEVEL UP",
      detail: verdict.lead ? `Install a node — ${verdict.label}` : "Install a node — pick a lane",
      tone: "build",
    };
  }
  if (snap.bounty) {
    return {
      title: "HUNT",
      detail: `Priority target · ${snap.bounty.remainingS.toFixed(0)}s · +${snap.bounty.bonusScore}`,
      tone: "hunt",
    };
  }
  const contract = activeSortieContract(snap.activeSortieContract);
  if (contract?.requiredBossArenas && snap.bossArenasCleared < contract.requiredBossArenas) {
    return {
      title: contract.hudLabel,
      detail: `Destroy arena guardian${contract.requiredBossArenas === 1 ? "" : "s"} · ${snap.bossArenasCleared}/${contract.requiredBossArenas}`,
      tone: "hunt",
    };
  }
  const ops = banked.dailyOps?.ops ?? [];
  const almost = ops
    .filter((op) => !op.claimed && op.progress < op.target)
    .sort((a, b) => b.progress / b.target - a.progress / a.target)[0];
  if (almost && almost.progress / almost.target >= 0.5) {
    return {
      title: "DAILY",
      detail: `${almost.title} ${almost.progress}/${almost.target}`,
      tone: "ops",
    };
  }
  if (snap.runInventory.salvage >= 28) {
    return {
      title: "EXTRACT",
      detail: `${snap.runInventory.salvage} scrap aboard — hold the pad`,
      tone: "extract",
    };
  }
  if (snap.incursionActive) {
    return {
      title: "INCURSION",
      detail: snap.incursionLabel || "Survive the wave",
      tone: "hunt",
    };
  }
  const openOp = ops.find((op) => !op.claimed);
  if (openOp) {
    return {
      title: "SORTIE",
      detail: `${openOp.title} ${openOp.progress}/${openOp.target}`,
      tone: "ops",
    };
  }
  return {
    title: "SORTIE",
    detail: "Farm scrap, keep the combo, extract alive",
    tone: "ops",
  };
}
